import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { MessagesSquare, Send, Plus, X } from "lucide-react";

const ROLE_LABEL = { SUPER_ADMIN: "Matriz", FRANQUICIA: "Franquicia", PROFESOR: "Profesor", ALUMNO: "Alumno" };

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function waLink(phone, fromName) {
  const p = (phone || "").replace(/[^0-9]/g, "");
  const text = encodeURIComponent(`Hola, soy ${fromName} de Natur Autoescuela. Te he escrito por el chat interno de Natur Academy: ${window.location.origin}/app/mensajes — entra con tus credenciales para responderme.`);
  return `https://wa.me/${p}?text=${text}`;
}

export default function Messages() {
  const { user } = useAuth();
  const isStaff = user.role !== "ALUMNO";
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [picking, setPicking] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [teacher, setTeacher] = useState(null);

  const loadConvs = () => api.get("/chat/conversations").then((r) => setConvs(r.data)).catch(() => {});
  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 5000);
    if (isStaff) api.get("/users").then((r) => setContacts(r.data.filter((u) => u.id !== user.id)));
    else api.get("/my-teacher").then((r) => setTeacher(r.data.teacher ? { ...r.data.teacher } : null)).catch(() => {});
    return () => clearInterval(t);
  }, []);

  const open = async (userId) => {
    try {
      const r = await api.post("/chat/conversations", { user_id: userId });
      setActive(r.data);
      setPicking(false);
      loadConvs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openTeacherChat = async () => {
    const me = await api.get("/auth/me");
    if (!me.data.teacher_id) { toast.error("No tienes profesor asignado todavía"); return; }
    open(me.data.teacher_id);
  };

  return (
    <div className="fade-up" data-testid="messages-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1 flex items-center gap-2"><MessagesSquare className="text-orange-600" /> Mensajes</h1>
      <p className="text-neutral-500 mb-6">{isStaff ? "Conversa con tus alumnos sin salir de la plataforma; puedes invitarles también por WhatsApp." : "Habla directamente con tu profesor."}</p>
      <div className="grid md:grid-cols-[300px,1fr] gap-4 items-start">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-3 border-b border-neutral-100 flex items-center justify-between">
            <p className="text-sm font-semibold">Conversaciones</p>
            {isStaff ? (
              <button data-testid="chat-new-btn" onClick={() => setPicking(!picking)} className="text-orange-600 hover:text-orange-700"><Plus size={17} /></button>
            ) : (
              <button data-testid="chat-teacher-btn" onClick={openTeacherChat} className="text-xs font-semibold text-orange-700 hover:text-orange-800">Escribir a mi profesor</button>
            )}
          </div>
          {picking && (
            <div className="p-3 border-b border-neutral-100 bg-neutral-50 max-h-64 overflow-y-auto" data-testid="chat-contact-picker">
              {contacts.map((c) => (
                <button key={c.id} data-testid="chat-contact-option" onClick={() => open(c.id)}
                        className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-white">
                  {c.name} <span className="text-[11px] text-neutral-400">· {ROLE_LABEL[c.role]}</span>
                </button>
              ))}
              {contacts.length === 0 && <p className="text-xs text-neutral-500 p-2">Sin contactos disponibles.</p>}
            </div>
          )}
          <div className="divide-y divide-neutral-100 max-h-[55vh] overflow-y-auto">
            {convs.map((c) => (
              <button key={c.id} data-testid="chat-conversation-row" onClick={() => setActive(c)}
                      className={`w-full text-left p-3 hover:bg-neutral-50 ${active?.id === c.id ? "bg-orange-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium truncate">{c.other?.name}</p>
                  {c.unread > 0 && <span data-testid="chat-unread-badge" className="text-[10px] font-bold bg-orange-600 text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
                </div>
                <p className="text-xs text-neutral-400 truncate">{c.last_message || "Sin mensajes"}</p>
              </button>
            ))}
            {convs.length === 0 && <p className="p-4 text-xs text-neutral-500">Todavía no hay conversaciones.</p>}
          </div>
        </div>
        {active ? <Thread key={active.id} conv={active} user={user} onClose={() => { setActive(null); loadConvs(); }} />
                : <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-10 text-center text-sm text-neutral-400">Selecciona o crea una conversación.</div>}
      </div>
    </div>
  );
}

function Thread({ conv, user, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const isStaff = user.role !== "ALUMNO";
  const load = () => api.get(`/chat/conversations/${conv.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [conv.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${conv.id}/messages`, { content: text });
      setText("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm flex flex-col h-[65vh]" data-testid="chat-thread">
      <div className="p-3 border-b border-neutral-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">{conv.other?.name}</p>
          <p className="text-[11px] text-neutral-400">{ROLE_LABEL[conv.other?.role]}</p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && conv.other?.phone && (
            <a data-testid="chat-whatsapp-invite" href={waLink(conv.other.phone, user.name)} target="_blank" rel="noreferrer"
               className="text-xs font-semibold bg-green-100 text-green-800 hover:bg-green-200 px-3 py-1.5 rounded-full transition-colors">
              Invitar por WhatsApp
            </a>
          )}
          {isStaff && !conv.other?.phone && (
            <span className="text-[11px] text-neutral-400" data-testid="chat-no-phone-note">Sin teléfono para WhatsApp</span>
          )}
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="chat-close-btn"><X size={16} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[75%] ${m.sender_id === user.id ? "ml-auto" : ""}`} data-testid="chat-message">
            <div className={`rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.sender_id === user.id ? "bg-orange-600 text-white rounded-br-sm" : "bg-neutral-100 rounded-bl-sm"}`}>
              {m.content}
            </div>
            <p className={`text-[10px] text-neutral-400 mt-0.5 ${m.sender_id === user.id ? "text-right" : ""}`}>{fmtTime(m.date)}</p>
          </div>
        ))}
        {msgs.length === 0 && <p className="text-xs text-neutral-400 text-center mt-8">Escribe el primer mensaje.</p>}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-neutral-100 flex gap-2">
        <textarea data-testid="chat-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Escribe un mensaje…"
                  className="flex-1 resize-none rounded-xl border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
        <Button data-testid="chat-send-btn" onClick={send} disabled={sending || !text.trim()}
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-10 w-10 p-0"><Send size={16} /></Button>
      </div>
    </div>
  );
}
